import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useLanguage, LANGS } from "../context/LanguageContext";
import { LEGAL } from "../config/legal";

const SITE = LEGAL.website; // https://lifeisaproject.global

/**
 * useHreflang — inserts <link rel="alternate" hreflang="…"> tags into
 * <head> for the current route. Because the site renders localized
 * content from a single URL, each language variant points to the same
 * canonical URL with a `?lang=xx` marker so Google can distinguish
 * them. LanguageProvider (below) reads `?lang=` on load and applies
 * it, so the marker is functional as well as metadata.
 *
 * Also emits a canonical link and the x-default alternate.
 */
export default function useHreflang() {
    const { lang } = useLanguage();
    const { pathname } = useLocation();

    useEffect(() => {
        const marker = "data-liap-hreflang";
        // Wipe previous tags
        document.head.querySelectorAll(`link[${marker}]`).forEach((n) => n.remove());

        const canonical = `${SITE}${pathname}`;
        const add = (attrs) => {
            const el = document.createElement("link");
            Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
            el.setAttribute(marker, "1");
            document.head.appendChild(el);
        };

        // canonical points to the current URL (no query — the language
        // is remembered by the visitor's own preference)
        add({ rel: "canonical", href: canonical });

        // CMS detail pages with linked localized versions manage their own
        // content-level hreflang (useContentHreflang) — when that override
        // is present, keep the canonical but skip the generic alternates.
        if (document.head.querySelector("link[data-liap-content-hreflang]")) return;

        // hreflang alternates — one per supported language + x-default
        LANGS.forEach((code) => {
            add({ rel: "alternate", hreflang: code, href: `${canonical}?lang=${code}` });
        });
        add({ rel: "alternate", hreflang: "x-default", href: canonical });

        // og:locale reflects the current language (Facebook / LinkedIn)
        const ogLocale = { en: "en_US", es: "es_ES", nl: "nl_NL", fr: "fr_FR" }[lang] || "en_US";
        let og = document.head.querySelector('meta[property="og:locale"]');
        if (og) og.setAttribute("content", ogLocale);
        // html lang stays in sync via LanguageContext already
    }, [lang, pathname]);
}
